// zLayers — the app's stacking tiers, named and ordered.
//
// Every overlay in this app used to pick its own z-index literal, and the
// numbers drifted into an order nobody could see: 1000 / 1100 / 1200 / 1500 /
// 2000 / 6000 / 6500. That is fine right up until two of them meet, and then
// the failure is SILENT — the element mounts, occupies the right rectangle,
// answers `getComputedStyle`, and is simply never painted where a user can
// reach it.
//
// The bug this module was written for: the material-graph texture picker.
// It portals to <body> at z-index 1200; MaterialGraphModal had been raised to
// 6500 to clear the app menubar. Measured in the running app —
// `document.elementFromPoint` at the picker's own centre returned
// `div.react-flow__pane`, the modal's canvas. Clicking the texture swatch
// opened a list that painted behind the editor, so the slot read as dead.
//
// The rule that prevents the whole class:
//
//   A POPOVER IS ALWAYS ABOVE THE SURFACE THAT OPENED IT.
//
// A popover (picker, dropdown, anchored menu) is transient, is dismissed by
// its own outside-click handler, and is meaningless when covered. There is no
// surface it should lose to — so it sits above the topmost surface tier rather
// than at a number that happens to beat today's callers. A fullscreen editor
// that later needs to clear something new raises FULLSCREEN_EDITOR, and every
// popover keeps working for free.
//
// Not every overlay in the app is migrated. The tiers below describe what is
// already there, so a new call site can join the scale instead of inventing an
// eighth literal; the unmigrated ones are unchanged and still correct at their
// own numbers.
export const Z = {
  // In-panel overlays: modal dialogs owned by a panel (folder picker, prefab
  // edit), inline editor chrome.
  PANEL_OVERLAY: 1000,
  // Right-click menus and the asset context menu.
  CONTEXT_MENU: 2000,
  // A submenu must clear its own parent menu, nothing more.
  CONTEXT_SUBMENU: 2001,
  // The app menubar strip and its dropdowns.
  APP_CHROME: 6000,
  // Fullscreen takeovers that deliberately cover the chrome while open.
  // Currently UNUSED: the material graph, its last holder, is a FloatingWindow
  // now. Kept as the documented slot between the chrome and the window band so
  // a future one does not invent an eighth literal — and because the popover
  // rule below is stated against the whole scale, not against live callers.
  FULLSCREEN_EDITOR: 6500,
  // Floating windows (windowStore.WINDOW_Z_BASE). Mirrored here so the ordering
  // is visible in ONE place; windowStore stays the owner of the value, and
  // zLayers.test.mjs asserts the two agree rather than trusting this copy.
  WINDOW_BASE: 6600,
  // Anchored popovers. Above every surface tier INCLUDING the window band, by
  // the rule above — the texture picker opens over the material graph window.
  POPOVER: 7000,
}

export default Z
